/* eslint-disable react/jsx-no-undef */
/* eslint-disable no-unused-vars */
import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Input,
  message,
  Select,
} from "antd";
import React, { useEffect } from "react";
import { faker } from "@faker-js/faker";
import { copyObjects } from "../../util/copyObjects";
import { useEmergencies } from "../../hooks/useEmergencies";
import { useAllResponderCompanies } from "../../hooks/useResponderCompany";

type NewAdminFormProp = {
  onFormFinish: (
    user: IUser,
    role: number | null,
    emergencyId: number | null,
    responderCompanyId?: number | null
  ) => void;
  onModelClose: () => void;
  isLoading: boolean;
  isSuccess: boolean;
};

const NewAdminForm = ({
  onFormFinish,
  onModelClose,
  isLoading,
  isSuccess,
}: NewAdminFormProp) => {
  const [form] = Form.useForm();
  const {
    data: emergencies,
    isLoading: loadingEmergencies,
    isFetching: refetchingEmergencies,
  } = useEmergencies();
  const {
    data: responderCompanies,
    isLoading: loadingResponderCompanies,
    isFetching: refetchingResponderCompanies,
  } = useAllResponderCompanies();

  const onFinish = async (formValues: any) => {
    const values: IUser = copyObjects(formValues, {
      userTypeId: formValues.role ? formValues.role : 21, // should change to either 21, 22 or 26
      password: faker.internet.password(),
      userCode: faker.string.alphanumeric(4),
    });

    await onFormFinish(
      values,
      formValues.role,
      formValues.emergencyId,
      formValues.responderCompanyId
    );
  };

  const handleClose = () => {
    form.resetFields();
    onModelClose();
  };

  const onFinishFailed = (errorInfo: any) => {
    message.error("Could not create user, please try again.");
  };

  useEffect(() => {
    if (isSuccess) {
      form.resetFields();
    }
  }, [isSuccess]);

  const prefixSelector = (
    <Form.Item name="prefix" noStyle>
      <Select style={{ width: 70 }}>
        <Select.Option value="+27">+27</Select.Option>
      </Select>
    </Form.Item>
  );

  return (
    <Form
      form={form}
      name="newUser"
      onFinish={onFinish}
      // onFinishFailed={onFinishFailed}
      layout="vertical"
      style={{ width: "100%" }}
      className="grid"
    >
      <div className="col-12 md:col-6">
        <Form.Item
          name="firstName"
          label="First Name"
          rules={[
            { required: true, message: "Please input the user's first name!" },
          ]}
          className="mb-1"
        >
          <Input size="large" placeholder="Amanda" />
        </Form.Item>
      </div>

      <div className="col-12 md:col-6">
        <Form.Item
          name="lastName"
          label="Last Name"
          rules={[
            { required: true, message: "Please input the user's last name!" },
          ]}
          className="mb-1"
        >
          <Input size="large" placeholder="Neli" />
        </Form.Item>
      </div>

      <div className="col-12 md:col-6">
        <Form.Item
          name="emailAddress"
          label="Email"
          rules={[
            { required: true, message: "Please input the user's email!" },
            { type: "email", message: "Please input a valid email!" },
          ]}
          className="mb-1"
        >
          <Input size="large" placeholder="Amando@gmail.com" />
        </Form.Item>
      </div>

      <div className="col-12 md:col-6">
        <Form.Item
          name="phoneNumber"
          label="Phone Number"
          rules={[
            {
              required: true,
              message: "Please enter user's phone number!",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                // const phoneNumberPattern = /^(\+27|0)[6-8][0-9]{8}$/;
                const phoneNumberPattern = /^\+?[1-9]\d{1,14}$/;
                if (phoneNumberPattern.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Please enter a valid phoneNumber!")
                );
              },
            }),
          ]}
          className="mb-1"
        >
          <Input size="large" placeholder="+2763256392" />
        </Form.Item>
      </div>

      <div className="col-12 md:col-6">
        <Form.Item
          name="dateOfBirth"
          label="Date of birth"
          rules={[
            { required: true, message: "Please enter user's date of birth!" },
          ]}
          className="mb-3"
        >
          <DatePicker size="large" style={{ width: "100%" }} />
        </Form.Item>
      </div>

      <div className="col-12 md:col-6">
        <Form.Item
          name="gender"
          label="Gender"
          rules={[
            { required: true, message: "Please select the user's gender!" },
          ]}
          className="mb-3"
        >
          <Select placeholder="Select user's gender" size="large">
            <Select.Option value="Male">Male</Select.Option>
            <Select.Option value="Female">Female</Select.Option>
            <Select.Option value="Other">Other</Select.Option>
          </Select>
        </Form.Item>
      </div>

      <div className="col-12">
        <Form.Item
          name="role"
          label="Admin Role"
          rules={[
            { required: true, message: "Please select the user's role!" },
          ]}
          className="mb-3"
        >
          <Select placeholder="Select admin role" size="large">
            <Select.Option value={21}>Super Admin</Select.Option>
            <Select.Option value={22}>Emergency Admin</Select.Option>
            <Select.Option value={26}>Responder Admin</Select.Option>
          </Select>
        </Form.Item>
      </div>

      <div className="col-12">
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.role !== currentValues.role
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("role") === 22 ? (
              <Form.Item
                name="emergencyId"
                label="Emergency"
                rules={[
                  {
                    required: true,
                    message: "Please select the user's emergency!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Emergency"
                  size="large"
                  loading={loadingEmergencies || refetchingEmergencies}
                >
                  {emergencies?.map((item: IEmergency) => (
                    <Select.Option
                      value={item.id}
                      key={`key-emergencies-${item.id}`}
                    >
                      {item.emergencyType}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </div>

      <div className="col-12">
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.role !== currentValues.role
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("role") === 26 ? (
              <Form.Item
                name="responderCompanyId"
                label="Responder Company"
                rules={[
                  {
                    required: true,
                    message: "Please select the responder company!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Responder Company"
                  size="large"
                  loading={loadingResponderCompanies || refetchingResponderCompanies}
                >
                  {responderCompanies?.map((item: IBasicResponderCompanyDto) => (
                    <Select.Option
                      value={item.id}
                      key={`key-responder-company-${item.id}`}
                    >
                      {item.companyName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </div>

      <Form.Item className="w-12 mb-1">
        <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
          <Button
            type="primary"
            ghost
            onClick={handleClose}
            size="large"
            className="w-3"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            size="large"
            className="w-3 bg-[#1757a2] shadow-none"
          >
            Save
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default NewAdminForm;
